import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import MainDialogLayout from '@/components/ui/MainDialogLayout/MainDialogLayout';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import { SupportRequestDtoIn } from '@/models/SupportRequestDtoIn';
import { UpdateSupportRequestDtoIn } from '@/models/in/UpdateSupportRequestDtoIn';
import { usePutAuthApi } from '@/hooks/UseApiCalls';
import useFundContext from '@/contexts/Fund/useFundContext';
import { getRepresentativeActionErrorMessage } from '@/helpers/apiErrorHelper';
import styles from './EditSupportRequestDialog.module.css';

interface EditSupportRequestDialogProps {
  open: boolean;
  onClose: () => void;
  request: SupportRequestDtoIn | null;
  onSuccess: () => void;
}

/**
 * SPR-1054: representative-facing amount edit for a request still awaiting signatures. Editing
 * resets any signatures already collected (see SupportsService.UpdateSupportRequestAsync).
 * Beneficiary switching is deliberately out of scope here - it needs a full supported/payment-info
 * picker (see newSupportRequest/steps/RequiredDetails.tsx) that wasn't warranted for this first
 * pass; the backend endpoint already accepts supportedId/paymentInfoId for when that's added.
 */
const EditSupportRequestDialog = ({ open, onClose, request, onSuccess }: EditSupportRequestDialogProps) => {
  const { t } = useTranslation();
  const { currentFund } = useFundContext();

  const [amount, setAmount] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isSucceeded, setIsSucceeded] = useState(false);

  const { mutate: updateRequest, isPending: isSubmitting } = usePutAuthApi<void, object, UpdateSupportRequestDtoIn>(
    `funds/${currentFund?.id}/support-requests/${request?.correlationId}`
  );

  useEffect(() => {
    if (open && request) {
      setAmount(String(request.amount));
      setError(null);
      setIsSucceeded(false);
    }
  }, [open, request]);

  if (!request) return null;

  const numericAmount = Number(amount);
  // Submitting an unchanged amount would no-op on the backend (nothing to reset), but the success
  // screen always claims signatures were reset - disable Save so that message stays accurate.
  const isUnchanged = numericAmount === request.amount;

  const handleSubmit = () => {
    if (!currentFund?.id) return;
    setError(null);

    if (!numericAmount || numericAmount <= 0) {
      setError(t('editSupportRequest.invalidAmount'));
      return;
    }

    updateRequest(
      { body: { requestedAmount: numericAmount } },
      {
        onSuccess: () => {
          setIsSucceeded(true);
          onSuccess();
        },
        onError: (err) => {
          setError(getRepresentativeActionErrorMessage(err, t, 'editSupportRequest.error'));
        },
      }
    );
  };

  return (
    <MainDialogLayout
      open={open}
      onOpenChange={(isOpen: boolean) => {
        if (!isOpen) onClose();
      }}
      title={t('editSupportRequest.title')}
    >
      <div className={styles.content}>
        {isSucceeded ? (
          <div className={styles.successContent}>
            <p className={styles.successMessage}>{t('editSupportRequest.successMessage')}</p>
            <div className={styles.actions}>
              <Button variant="primary" className={styles.actionButton} onClick={onClose}>
                {t('editSupportRequest.close')}
              </Button>
            </div>
          </div>
        ) : (
          <>
            <p className={styles.confirmText}>{t('editSupportRequest.confirmText')}</p>
            <div className={styles.section}>
              <label className={styles.sectionLabel}>{t('editSupportRequest.amountLabel')}:</label>
              <Input className="w-full" type="number" value={amount} onChange={(e) => setAmount(e.target.value)} />
            </div>
            {error && <p className={styles.error}>{error}</p>}
            <div className={styles.actions}>
              <Button variant="white" className={styles.secondaryButton} onClick={onClose} disabled={isSubmitting}>
                {t('editSupportRequest.cancel')}
              </Button>
              <Button variant="primary" className={styles.actionButton} onClick={handleSubmit} disabled={isSubmitting || isUnchanged}>
                {isSubmitting ? t('loading') : t('editSupportRequest.confirm')}
              </Button>
            </div>
          </>
        )}
      </div>
    </MainDialogLayout>
  );
};

export default EditSupportRequestDialog;
